import React, { useMemo } from 'react';
import { SCENARIO_VISIBILITY_OPTIONS } from '../utils/vectorBasketDisplay';
import { DATA_SOURCE_TYPE_LABELS } from '../utils/vectorScenarioRegistry';
import { parseCsvPreviewTable } from '../utils/strategySubmissionCsvPreview';

export function ReviewRow({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <>
      <dt>{label}</dt>
      <dd className={mono ? 'admin-submission-review-mono' : undefined}>{value}</dd>
    </>
  );
}

function dataSourceLabel(type, config = {}) {
  const rawType = String(type || '').trim();
  if (rawType === 'webhook') {
    const parser = String(config.parser || '').trim();
    if (parser === 'kairos_daily') return DATA_SOURCE_TYPE_LABELS.webhook_kairos;
    if (parser === 'nt8_trade_alert') return DATA_SOURCE_TYPE_LABELS.webhook_nt8_alert;
    if (parser === 'tradingview_orders') return DATA_SOURCE_TYPE_LABELS.webhook_tradingview;
    return 'Live webhook';
  }
  return DATA_SOURCE_TYPE_LABELS[rawType] || rawType || '—';
}

function categoryLabel(category) {
  return category === 'futures' ? 'Futures' : 'Stocks';
}

function visibilityLabel(visibility) {
  return SCENARIO_VISIBILITY_OPTIONS.find((o) => o.id === visibility)?.label || visibility;
}

export function CsvPreviewTable({ csvText, filename }) {
  const preview = useMemo(() => parseCsvPreviewTable(csvText), [csvText]);
  if (!preview?.columns?.length) {
    return <p className="admin-submission-csv-preview-empty">Could not parse CSV preview.</p>;
  }

  const captionParts = [];
  if (filename) captionParts.push(filename);
  captionParts.push(`${preview.totalRows} row${preview.totalRows === 1 ? '' : 's'}`);
  if (preview.truncatedRows || preview.truncatedCols) {
    captionParts.push('showing preview only');
  }

  return (
    <div className="admin-submission-csv-preview">
      <p className="admin-submission-csv-preview-caption">{captionParts.join(' · ')}</p>
      <div className="admin-live-strategy-spreadsheet-scroll admin-submission-csv-preview-scroll">
        <table className="admin-live-strategy-spreadsheet admin-live-strategy-spreadsheet--wide admin-submission-csv-preview-table">
          <thead>
            <tr>
              <th className="admin-live-strategy-spreadsheet-corner" aria-hidden="true" />
              {preview.columns.map((col, idx) => (
                <th key={`${idx}-${col}`} title={col}>{col}</th>
              ))}
              {preview.truncatedCols ? <th title="More columns">…</th> : null}
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row, rowIdx) => (
              <tr key={`csv-row-${rowIdx}`}>
                <th scope="row" className="admin-live-strategy-spreadsheet-row-num">{rowIdx + 1}</th>
                {row.map((cell, colIdx) => (
                  <td key={`${rowIdx}-${colIdx}`} title={cell}>{cell}</td>
                ))}
                {preview.truncatedCols ? <td aria-hidden="true">…</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.truncatedRows || preview.truncatedCols ? (
        <p className="admin-submission-csv-preview-note">
          Preview limited to {preview.rows.length} rows
          {preview.truncatedCols ? ` and ${preview.columns.length} columns` : ''}
          {preview.totalCols > preview.columns.length
            ? ` (${preview.totalCols} columns total)`
            : ''}
          .
        </p>
      ) : null}
    </div>
  );
}

export function AdminSubmissionDetailSummary({ submission, statusText }) {
  const config = submission?.dataSourceConfig || {};
  const hasCsv = Boolean(submission?.hasCsv);
  const csvLabel = hasCsv
    ? (submission.csvFilename || 'CSV attached')
    : 'Webhook only — no CSV baseline';

  return (
    <div className="admin-submission-review admin-submission-review--detail">
      <div className="admin-submission-review-hero">
        <div className="admin-submission-review-hero-main">
          <p className="admin-submission-review-eyebrow">Submission review</p>
          <h3 className="admin-submission-review-title">{submission.displayName}</h3>
          <p className="admin-submission-review-subtitle">
            {submission.entitySlug}
            {' · '}
            <span className="admin-submission-review-mono">{submission.strategyName}</span>
            {' · '}
            {submission.submittedByEmail}
          </p>
        </div>
        <div className="admin-submission-review-badges">
          {statusText ? (
            <span className={`admin-submission-review-status is-${submission.status}`}>
              {statusText}
            </span>
          ) : null}
          <span className="admin-submission-review-badge admin-submission-review-badge--kind">
            {categoryLabel(submission.category)}
          </span>
          {config.displaySymbol ? (
            <span className="admin-submission-review-badge admin-submission-review-badge--symbol">
              {config.displaySymbol}
            </span>
          ) : null}
          <span className={`admin-submission-review-badge admin-submission-review-badge--source ${hasCsv ? 'is-csv' : 'is-webhook'}`}>
            {hasCsv ? 'CSV upload' : 'Live webhook'}
          </span>
        </div>
      </div>

      <div className="admin-submission-review-grid">
        <section className="admin-submission-review-card">
          <h4>Identity</h4>
          <dl className="admin-submission-review-dl">
            <ReviewRow label="Brand" value={submission.entitySlug} />
            <ReviewRow label="Display name" value={submission.displayName} />
            <ReviewRow label="Slug" value={submission.proposedSlug} mono />
            <ReviewRow label="Data name" value={submission.strategyName} mono />
            <ReviewRow label="Submitter" value={submission.submittedByName || submission.submittedByEmail} />
          </dl>
        </section>

        <section className="admin-submission-review-card">
          <h4>Strategy setup</h4>
          <dl className="admin-submission-review-dl">
            <ReviewRow label="Category" value={categoryLabel(submission.category)} />
            <ReviewRow label="Data source" value={dataSourceLabel(submission.dataSourceType, config)} />
            <ReviewRow label="Visibility" value={visibilityLabel(submission.visibilityRequested)} />
            <ReviewRow label="Symbol" value={config.displaySymbol || null} mono />
          </dl>
        </section>

        <section className="admin-submission-review-card admin-submission-review-card--wide">
          <h4>Data &amp; baseline</h4>
          <dl className="admin-submission-review-dl">
            <ReviewRow label="CSV file" value={csvLabel} mono={hasCsv && Boolean(submission.csvFilename)} />
            {!hasCsv && config.kairosSymbol ? (
              <ReviewRow label="Kairos symbol" value={config.kairosSymbol} mono />
            ) : null}
            {!hasCsv && config.strategyId ? (
              <ReviewRow label="TradingView strategy id" value={config.strategyId} mono />
            ) : null}
            {!hasCsv && config.nt8StrategyName ? (
              <ReviewRow label="NT8 strategy" value={config.nt8StrategyName} />
            ) : null}
            <ReviewRow
              label="Submitted"
              value={submission.createdAt ? new Date(submission.createdAt).toLocaleString() : null}
            />
          </dl>
        </section>

        {(submission.description || submission.submitterNotes) ? (
          <section className="admin-submission-review-card admin-submission-review-card--wide">
            <h4>Notes from submitter</h4>
            <dl className="admin-submission-review-dl">
              <ReviewRow label="Description" value={submission.description} />
              <ReviewRow label="Notes for reviewer" value={submission.submitterNotes} />
            </dl>
          </section>
        ) : null}
      </div>
    </div>
  );
}
